<!-- 使用教學： /instructions/:type? -->
<template>
  <div class="layout_wrapper">
    <div class="container_wrapper">
      <h3 class="page_main_title">
        {{ categoryTitle }}
      </h3>

      <TabMenu
        :model="categoryItems"
        :active="0"
      />

      <div class="common_setting_wrapper">
        <div
          v-if="currentDescription"
          class="description_block"
        >
          <p
            v-for="(text, num) in currentDescription.split('\n')"
            :key="num"
            class="content_text"
            @click.prevent="handlePxplusLink"
            v-html="text"
          />
        </div>

        <div
          v-for="(item, idx) in Object.keys(currentContent.data)"
          :key="idx"
          class="item_block"
        >
          <div class="title_block">
            <p class="step_title">
              {{ item }}
            </p>

            <p class="main_title">
              {{ currentContent.data[item].title }}
            </p>

            <p
              v-for="(text, num) in currentContent.data[item].content.context.split('\n')"
              :key="num"
              class="content_text"
              v-html="text"
            />
          </div>

          <div class="images_container">
            <Swiper
              class="image_block swiper_slider_wrapper"
              :pagination="currentContent.data[item].content.images.length > 1 ? { dynamicBullets: true } : false"
              :space-between="24"
              :observer="true"
            >
              <SwiperSlide
                v-for="(imageUrl, num) in currentContent.data[item].content.images"
                :key="num"
                class="swiper_slider_item"
              >
                <div class="img_block">
                  <picture>
                    <source
                      media="(min-width: 768)"
                      :srcset="String(imageUrl).replace('_2', '_3')"
                    >
                    <source
                      media="(max-width: 320px)"
                      :srcset="String(imageUrl).replace('_2', '_1')"
                    >
                    <img
                      alt=""
                      class="menu_icon"
                      :src="imageUrl"
                    >
                  </picture>
                </div>
              </SwiperSlide>
            </Swiper>
          </div>
        </div>
      </div>
    </div>

    <component :is="dialogName" />
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { onBeforeRouteUpdate } from 'vue-router';
import { getInstructionList } from '@/libs/API/apis';
import { AppDialog } from '@/components/Modal/';
import SwiperCore, { Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { setGaEvent, isObjKeyExist, handlePxplusLink } from '@/libs/global';
import TabMenu from 'primevue/tabmenu';
import useCommon from '@/libs/globalComposition';
import 'swiper/swiper.scss';

export default {
  name: 'Instructions',
  components: {
    TabMenu,
    Swiper,
    SwiperSlide,
    AppDialog
  },
  setup () {
    SwiperCore.use([Pagination]);

    const { currentRoute, dialogName } = useCommon();
    const categoryItems = ref([]);
    const categoryTitle = ref('');
    const currentDescription = ref('');

    const instructionFileContent = reactive({ data: {} });
    const descriptionContent = reactive({ data: {} });
    const currentContent = reactive({ data: {} });

    const getTabAction = (path) => {
      const currentTab = path.split('/');

      categoryTitle.value = decodeURI(currentTab[2]);
      currentContent.data = instructionFileContent.data[categoryTitle.value];
      currentDescription.value = descriptionContent.data[categoryTitle.value]?.content.context || '';

      setGaEvent('tutorial', { category: categoryTitle.value });
    };

    onBeforeRouteUpdate((to) => {
      getTabAction(to.path);
    });

    onMounted(() => {
      getInstructionList(currentRoute.params.source)
        .then((res) => {
          // 取得功能說明區塊的 object key value
          const descriptionKey = '功能說明';

          Object.keys(res).forEach((categoryName, idx) => {
            categoryItems.value.push({ label: categoryName, to: `/instructions/${encodeURI(categoryName)}` });

            if (isObjKeyExist(res[categoryName], descriptionKey)) {
              descriptionContent.data[categoryName] = res[categoryName][descriptionKey];

              delete res[categoryName][descriptionKey];
            }

            instructionFileContent.data[categoryName] = res[categoryName];
          });

          getTabAction(currentRoute.path);
        });
    });

    return {
      dialogName,
      categoryItems,
      currentContent,
      categoryTitle,
      currentDescription,
      handlePxplusLink
    };
  }
};
</script>

<style scoped lang="scss">
@import '@/scss/common/common.scss';
@import '@/scss/common/swiper.scss';
@import '@/scss/common/tabs.scss';
@import '@/scss/common/layout.scss';

.layout_wrapper {
  .common_setting_wrapper {
    .description_block {
      margin: 30px 0px 70px;
      max-height: 78px;
      overflow: hidden;

      .content_text {
        margin: 0px;
      }
    }

    .item_block {
      align-items: center;
      display: flex;
      flex-wrap: wrap;
      margin-bottom: 70px;
      width: 100%;

      &:first-child {
        margin-top: 70px;
      }

      &:last-of-type {
        margin-bottom: 0px;
      }

      ::v-deep(.swiper_slider_wrapper) {
        .swiper-pagination {
          .swiper-pagination-bullet {
            height: 14px;
            width: 14px;
            margin: 0px 14px 0px 0px;
          }

          .swiper-pagination-bullet-active {
            background: $green;
          }
        }
      }

      .title_block {
        flex: 1;
        max-width: 435px;

        .step_title {
          @include font-size-bold-18;

          color: $primary_color;
          margin: 0px;
        }

        .main_title {
          @include font-size-bold-40;

          margin: 10px 0px 20px;
        }
      }

      .images_container {
        margin-left: 200px;
        width: 292px;

        .img_block {
          margin: 30px 0px;
          text-align: center;

          img {
            width: 100%;
          }
        }
      }
    }

    .content_text {
      @include font-size-18;

      color: $mono_80;
      margin: 10px 0px 0px;

      ::v-deep(.item_url) {
        color: $green;
        cursor: pointer;
        text-decoration: none;

        &:hover {
          text-decoration: underline;
        }
      }
    }
  }
}

@include RWD_1024 {
  .layout_wrapper {
    .common_setting_wrapper {
      .item_block {
        .images_container {
          margin-left: 100px;
        }
      }
    }
  }
}

@include RWD_768 {
  .layout_wrapper {
    .common_setting_wrapper {
      .description_block {
        max-height: 104px;
      }

      .item_block {
        .images_container {
          margin-left: 50px;
        }
      }
    }
  }
}

@include RWD_414 {
  .layout_wrapper {
    .common_setting_wrapper {
      .description_block {
        margin: 0px 0px 30px;
        max-height: 100%;
      }

      .item_block {
        margin-bottom: 50px;

        &:first-child {
          margin-top: 10px;
        }

        ::v-deep(.swiper_slider_wrapper) {
          .swiper-pagination {
            .swiper-pagination-bullet {
              height: 8px;
              width: 8px;
              margin: 0px 8px 0px 0px;
            }
          }
        }

        .title_block {
          max-width: 100%;

          .step_title {
            font-size: 13px;
            line-height: 22px;
          }

          .main_title {
            font-size: 22px;
            line-height: 32px;
            margin: 0px;
          }
        }

        .images_container {
          margin-left: 0px;
          width: 100%;

          .img_block {
            margin: 20px 0px;

            img {
              max-height: 450px;
              width: auto;
            }
          }
        }
      }

      .content_text {
        font-size: 16px;
      }
    }
  }
}
</style>
